import { computed, DestroyRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { GuidelinesHttpService } from 'guideline/domain';
import { pairwise, switchMap, tap } from 'rxjs';
import { MessagesService } from 'shared/domain';
import { tapAllResponses } from 'shared/util-common';

import { GuidelineEditTrackingDto, UserDto } from '@treetop/shared/model';

@Injectable()
export class ConcurrencyService implements OnDestroy {
  private guidelinesHttpService = inject(GuidelinesHttpService);
  private messagesService = inject(MessagesService);
  private destroyRef = inject(DestroyRef);

  private _currentlyEditingUsers = signal<UserDto[] | undefined>(undefined);
  private _refreshGuidelineButtonEnabled = signal(false);

  public currentlyEditingUsers = computed(() => this._currentlyEditingUsers());
  public refreshGuidelineButtonEnabled = computed(() => this._refreshGuidelineButtonEnabled());

  public _disabled = signal(false);
  public disabled = this._disabled.asReadonly();

  public loading = signal(false);

  private intervalId: ReturnType<typeof setInterval>;

  protected currentlyEditedByAnotherUser = toObservable(computed(() => this._currentlyEditingUsers()?.length > 0));

  private getCurrentlyEditing = rxMethod<string>((guidelineId$) =>
    guidelineId$.pipe(
      tap(() => {
        if (this._currentlyEditingUsers() === undefined) {
          this.loading.set(true);
        }
      }),
      switchMap((guidelineId) => this.guidelinesHttpService.currentlyEditing(guidelineId)),
      tapAllResponses<GuidelineEditTrackingDto[]>(
        (editTracking) => {
          const orderedTrackings = editTracking.sort(
            (a, b) => new Date(a.latestHeartbeat).getTime() - new Date(b.latestHeartbeat).getTime(),
          );
          const users = orderedTrackings.map((tracking) => tracking.user);

          this._currentlyEditingUsers.set(users);

          if (users.length > 0) {
            this._disabled.set(true);
          }

          if (users) this.loading.set(false);
        },
        (error) => {
          this.messagesService.showError(undefined, error);
          this.loading.set(false);
        },
      ),
    ),
  );

  public init(guidelineId: string) {
    this._disabled.set(false);

    this.getCurrentlyEditing(guidelineId);

    this._refreshGuidelineButtonEnabled.set(false);

    this.currentlyEditedByAnotherUser.pipe(pairwise(), takeUntilDestroyed(this.destroyRef)).subscribe(([prev, curr]) => {
      if (prev === true && curr === false) {
        this._refreshGuidelineButtonEnabled.set(true);
      } else {
        this._refreshGuidelineButtonEnabled.set(false);
      }
    });

    this.intervalId = setInterval(() => {
      this.getCurrentlyEditing(guidelineId);
    }, 5000);
  }

  public reset() {
    this._currentlyEditingUsers.set(undefined);
    this._refreshGuidelineButtonEnabled.set(false);
    this._disabled.set(false);
    this.loading.set(false);

    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  public ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
